import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, UserCog, Trash2, Shield, ShieldCheck } from "lucide-react";

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: "", password: "", full_name: "", role: "operator" });

  const load = async () => {
    try { const r = await api.get("/admin/users"); setItems(r.data); } catch {}
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data));
    load();
  }, []);

  const startNew = () => { setEditing(null); setForm({ username: "", password: "", full_name: "", role: "operator" }); setOpen(true); };
  const startEdit = (u) => { setEditing(u); setForm({ username: u.username, password: "", full_name: u.full_name || "", role: u.role || "operator" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const body = { full_name: form.full_name, role: form.role };
        if (form.password) body.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, body);
        toast.success("Utilizator actualizat");
      } else {
        await api.post("/admin/users", form);
        toast.success("Utilizator adaugat");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Eroare"); }
  };

  const toggleActive = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
    load();
  };

  const remove = async (u) => {
    if (!confirm(`Sterge utilizatorul ${u.username}?`)) return;
    await api.delete(`/admin/users/${u.id}`);
    toast.success("Sters"); load();
  };

  if (me && me.role !== "admin") {
    return <div className="text-center py-12 text-neutral-500">Numai administratorul are acces la aceasta pagina.</div>;
  }

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase">Angajati & utilizatori</h1>
          <p className="text-sm text-neutral-400 mt-1">Conturi pentru angajatii care folosesc panoul</p>
        </div>
        <Button onClick={startNew} data-testid="add-user-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold uppercase tracking-wider rounded-none">
          <Plus className="w-4 h-4 mr-2" /> Utilizator nou
        </Button>
      </div>

      <div className="bg-neutral-950 border border-neutral-800">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 border-b border-neutral-800">
            <tr className="text-left text-xs uppercase tracking-widest text-neutral-400">
              <th className="p-4">Utilizator</th>
              <th className="p-4">Rol</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actiuni</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-neutral-500">Nu exista utilizatori in plus inca. Doar contul tau principal.</td></tr>}
            {items.map((u) => (
              <tr key={u.id} className="border-b border-neutral-900">
                <td className="p-4">
                  <div className="font-medium">{u.username}</div>
                  {u.full_name && <div className="text-xs text-neutral-500 mt-0.5">{u.full_name}</div>}
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 ${u.role === "admin" ? "bg-[#00E5FF] text-black" : "bg-neutral-800 text-neutral-300"}`}>
                    {u.role === "admin" ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                    {u.role}
                  </span>
                </td>
                <td className="p-4">
                  <button onClick={() => toggleActive(u)} className={`text-[10px] uppercase tracking-widest px-2 py-1 ${u.is_active ? "bg-green-500/20 text-green-400 hover:bg-green-500/30" : "bg-red-500/20 text-red-400 hover:bg-red-500/30"}`}>
                    {u.is_active ? "Activ" : "Dezactivat"}
                  </button>
                </td>
                <td className="p-4 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => startEdit(u)} className="text-neutral-400 hover:text-[#00E5FF] p-1"><UserCog className="w-4 h-4" /></button>
                    <button onClick={() => remove(u)} className="text-neutral-500 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 bg-neutral-950 border border-neutral-800 p-4 text-xs text-neutral-400">
        <strong className="text-[#00E5FF]">Roluri:</strong> <span className="text-neutral-300">admin</span> = acces complet (vede tot + gestioneaza utilizatori). <span className="text-neutral-300">operator</span> = vede si gestioneaza reparatii, clienti, rezervari, dar nu poate edita utilizatori.
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-display uppercase">{editing ? `Editeaza ${editing.username}` : "Utilizator nou"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {!editing && (
              <Field label="Username *"><Input value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
            )}
            <Field label="Nume complet"><Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
            <Field label={editing ? "Parola noua (lasa gol = neschimbat)" : "Parola *"}>
              <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" />
            </Field>
            <Field label="Rol">
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger className="bg-neutral-900 border-neutral-800 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <DialogFooter>
              <Button type="submit" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none">{editing ? "Salveaza" : "Adauga"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-wider text-neutral-400">{label}</Label>
    {children}
  </div>
);
